/**
 * Brokerage setup guide, shown inside the app.
 * Copy follows the brokerage handbook: firm first, then team member.
 * Paths are the real realtor and teammember routes.
 */

export type OnboardingAudience = "firm" | "member";

export type OnboardingStep = {
  id: string;
  title: string;
  detail: string;
  /** Shown as the on-page tip while the user is on this screen. */
  hint: string;
  path: string;
  /** Route prefixes that mean "you are on this step". Exact match or prefix + "/". */
  match: string[];
  optional?: boolean;
  /** Steps that share a group count as one required task. Finish either one. */
  oneOf?: string;
  /** Kept out of the first list. Not needed in week one. */
  later?: boolean;
  /** Visiting the page is the whole task (Welcome). */
  autoDoneOnVisit?: boolean;
};

export type OnboardingProgress = {
  done: Record<string, boolean>;
  dismissedHints: Record<string, boolean>;
  collapsed: boolean;
  hidden: boolean;
};

const EMPTY: OnboardingProgress = {
  done: {},
  dismissedHints: {},
  collapsed: false,
  hidden: false,
};

const firm: OnboardingStep[] = [
  {
    id: "welcome",
    title: "Start on Welcome",
    detail: "This is the Brokerage Hub. The left menu is every next step.",
    hint: "Read this page, then follow the checklist in order. Login always brings you back here.",
    path: "/realtor/welcome",
    match: ["/realtor/welcome"],
    autoDoneOnVisit: true,
  },
  {
    id: "profile",
    title: "Save the company profile",
    detail: "Company name and company address.",
    hint: "Fill the company name and company address, then click Save. Orders and emails need the company name.",
    path: "/realtor/profile",
    match: ["/realtor/profile"],
  },
  {
    id: "roles",
    title: "Create a role",
    detail: "Example: Office Manager. Choose which menus they may open.",
    hint: "Click + Add Role. Name it and choose what that person may open, such as Offices, Agents, Orders, and Jobs. A team member needs a role before you invite them.",
    path: "/realtor/roles",
    match: ["/realtor/roles"],
  },
  {
    id: "offices",
    title: "Add an office and an address",
    detail: "At least one office, and one address inside it.",
    hint: "Click + Add Office. Save the name, type, phone, and address. Then open the office with the eye icon and add an address on the Office Address tab.",
    path: "/realtor/offices",
    match: ["/realtor/offices", "/realtor/office", "/realtor/view-office", "/realtor/officeAddresses"],
  },
  {
    id: "team",
    title: "Invite a team member",
    detail: "Pick the role and the office, then save.",
    hint: "Click + Add Teammember. Enter their details, pick a role, pick an office, and Save. They can then log in as a team member.",
    path: "/realtor/teammembers",
    match: ["/realtor/teammembers", "/realtor/RealtorTeammember", "/realtor/view-teammember"],
  },
  {
    id: "agents",
    title: "Add an agent",
    detail: "Only when an order is for that agent.",
    hint: "Skip this if you only order for the brokerage. If an order is for an agent, click + Add Agent and save. Open the agent to add an address.",
    path: "/realtor/agents",
    match: ["/realtor/agents", "/realtor/addAgent", "/realtor/editAgent", "/realtor/viewAgent"],
    optional: true,
  },
  {
    id: "vendors",
    title: "Connect a vendor",
    detail: "First products come from the vendor shop.",
    hint: "Find a signage vendor and click Connect. You can also Request a vendor who is not in the list. Create Order starts on this screen on purpose.",
    path: "/realtor/suppliers",
    match: [
      "/realtor/suppliers",
      "/realtor/signage",
      "/realtor/viewSignageProduct",
      "/realtor/viewSignageVariableProduct",
      "/realtor/viewProductVariant",
    ],
  },
  {
    id: "vendor-access",
    title: "Let a vendor see this office",
    detail: "Optional. Skip it and those people stay hidden from the vendor.",
    hint: "Open the office, go to Vendors Office Access, and click + Add permission. If you skip this, the vendor will not see this office’s team members and agents.",
    path: "/realtor/offices",
    match: ["/realtor/view-office", "/realtor/vendor-permissions"],
    optional: true,
  },
  {
    id: "order",
    title: "Place an order",
    detail: "Create Order opens Vendors. Then cart and checkout.",
    hint: "Click + Create Order. Pick the connected vendor, add products, then cart and checkout. Choose an agent only if the order is for that agent.",
    path: "/realtor/orders",
    match: ["/realtor/orders", "/realtor/order", "/realtor/orderDetails"],
    oneOf: "first-work",
  },
  {
    id: "install",
    title: "Add an installation",
    detail: "You do not need an order first.",
    hint: "Open Jobs, then Installations, and click + Add Installation. Fill the form and save. An order is not required.",
    path: "/realtor/jobs/installations",
    match: [
      "/realtor/jobs/installations",
      "/realtor/jobs/addInstallation",
      "/realtor/jobs/viewInstallation",
      "/realtor/jobs/editInstallation",
    ],
    oneOf: "first-work",
  },
  {
    id: "active",
    title: "See signs still out",
    detail: "Active Installations. Request a removal from here when you need one.",
    hint: "This list is what is still installed. Open a row to view it, or request a removal when the sign should come down.",
    path: "/realtor/active-installations",
    match: ["/realtor/active-installations"],
    later: true,
    optional: true,
  },
  {
    id: "removal",
    title: "Request a removal",
    detail: "Jobs, then Removal Requests.",
    hint: "Click + Add Removal, then save. Use the eye to view a request and the pencil to edit it.",
    path: "/realtor/jobs/removalRequests",
    match: ["/realtor/jobs/removalRequests", "/realtor/jobs/addRemovalRequest", "/realtor/jobs/editRemovalRequest", "/realtor/jobs/viewRemovalRequest"],
    later: true,
    optional: true,
  },
  {
    id: "payments",
    title: "Pay vendor bills",
    detail: "Batch Payments, once you have bills.",
    hint: "Click + New Payment Batch. Open a row for the report or receipt. Cancel only if that button is shown.",
    path: "/realtor/batchPayments",
    match: ["/realtor/batchPayments", "/realtor/batchPayment"],
    later: true,
    optional: true,
  },
  {
    id: "reports",
    title: "Open a report",
    detail: "View only. Installation, removal, and sales order summaries.",
    hint: "Reports are view only. The three summaries are Installation, Removal, and Sales Order.",
    path: "/realtor/installationSummaryReport",
    match: ["/realtor/installationSummaryReport", "/realtor/removalSummaryReport", "/realtor/salesOrderReport"],
    later: true,
    optional: true,
  },
  {
    id: "design",
    title: "Design requests",
    detail: "Custom artwork. Not required to start.",
    hint: "Use Design Requests when you need custom artwork. It is not part of the first-week setup.",
    path: "/realtor/design-requests",
    match: ["/realtor/design-requests"],
    later: true,
    optional: true,
  },
  {
    id: "inventory",
    title: "Your own inventory",
    detail: "Office and agent stock. First orders still come from the vendor.",
    hint: "Use this when the brokerage keeps its own signs. New users buy from the vendor first.",
    path: "/realtor/officeInventory",
    match: ["/realtor/officeInventory", "/realtor/agentInventory", "/realtor/brands", "/realtor/categories", "/realtor/inventory"],
    later: true,
    optional: true,
  },
];

const member: OnboardingStep[] = [
  {
    id: "welcome",
    title: "Start on Welcome",
    detail: "Your menu shows only what your role allows.",
    hint: "Read what you can do. Offices and Profile are always in the menu. Other items appear only if your role includes them.",
    path: "/teammember/welcome",
    match: ["/teammember/welcome"],
    autoDoneOnVisit: true,
  },
  {
    id: "profile",
    title: "Save your password and details",
    detail: "Your own profile. Not the company.",
    hint: "Set your password and your own details, then Save. Company fields belong to the firm.",
    path: "/teammember/profile",
    match: ["/teammember/profile"],
  },
  {
    id: "offices",
    title: "View your office",
    detail: "Look only. You cannot add, edit, or delete an office.",
    hint: "Click the eye icon to view your office. Add, edit, and delete are for the firm. If you need a change, ask the firm admin.",
    path: "/teammember/offices",
    match: ["/teammember/offices", "/teammember/view-office", "/teammember/office"],
  },
  {
    id: "agents",
    title: "Agents",
    detail: "Only if this menu is on your role. An agent is optional.",
    hint: "Add, view, edit, or delete an agent only if your role allows it. Skip agents unless the order is for one.",
    path: "/teammember/agents",
    match: ["/teammember/agents", "/teammember/addAgent", "/teammember/editAgent", "/teammember/viewAgent"],
    optional: true,
  },
  {
    id: "vendors",
    title: "Connect or shop a vendor",
    detail: "Only if Vendors is in your menu.",
    hint: "Browse vendors, click Connect, or open a vendor to shop. Create Order starts here: vendor, then products, then cart.",
    path: "/teammember/suppliers",
    match: ["/teammember/suppliers", "/teammember/signage"],
    optional: true,
  },
  {
    id: "order",
    title: "Place an order",
    detail: "One real task. Create Order opens Vendors.",
    hint: "Click + Create Order. That opens Vendors on purpose. Pick a vendor, add products, then cart and checkout. Pick an agent only if the order is for that agent.",
    path: "/teammember/orders",
    match: ["/teammember/orders", "/teammember/order", "/teammember/orderDetails"],
    oneOf: "first-work",
  },
  {
    id: "install",
    title: "Add an installation",
    detail: "One real task. An order is not required.",
    hint: "Open Jobs, then Installations, and click + Add Installation. You do not need an order first.",
    path: "/teammember/jobs/installations",
    match: [
      "/teammember/jobs/installations",
      "/teammember/jobs/addInstallation",
      "/teammember/jobs/viewInstallation",
      "/teammember/jobs/editInstallation",
    ],
    oneOf: "first-work",
  },
  {
    id: "active",
    title: "See signs still out",
    detail: "Only if Jobs is in your menu.",
    hint: "Active Installations shows what is still out. Request a removal from here when a sign should come down.",
    path: "/teammember/active-installations",
    match: ["/teammember/active-installations"],
    later: true,
    optional: true,
  },
  {
    id: "removal",
    title: "Request a removal",
    detail: "Only if Jobs is in your menu.",
    hint: "Open Removal Requests and click + Add Removal. Use the eye to view and the pencil to edit.",
    path: "/teammember/jobs/removalRequests",
    match: [
      "/teammember/jobs/removalRequests",
      "/teammember/jobs/addRemovalRequest",
      "/teammember/jobs/editRemovalRequest",
      "/teammember/jobs/viewRemovalRequest",
    ],
    later: true,
    optional: true,
  },
  {
    id: "payments",
    title: "Batch payments",
    detail: "Only if this menu is on your role.",
    hint: "Click + New Payment Batch when you are paying vendor bills. Open a row for the report or receipt.",
    path: "/teammember/batchPayments",
    match: ["/teammember/batchPayments", "/teammember/batchPayment"],
    later: true,
    optional: true,
  },
  {
    id: "reports",
    title: "Reports",
    detail: "View only, if the menu is there.",
    hint: "Reports are view only. If the menu is missing, your role does not include it. Ask the firm admin.",
    path: "/teammember/installationSummaryReport",
    match: ["/teammember/installationSummaryReport", "/teammember/removalSummaryReport", "/teammember/salesOrderReport"],
    later: true,
    optional: true,
  },
];

export const onboardingSteps: Record<OnboardingAudience, OnboardingStep[]> = {
  firm,
  member,
};

export function pathMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function longestMatchLength(pathname: string, step: OnboardingStep): number {
  let best = 0;
  for (const prefix of step.match) {
    if (pathMatches(pathname, prefix) && prefix.length > best) best = prefix.length;
  }
  return best;
}

/** Required items, with "do one of these" groups counted once. */
export function requiredSlots(steps: OnboardingStep[]): OnboardingStep[][] {
  const required = steps.filter((step) => !step.optional && !step.later);
  const seen = new Set<string>();
  const slots: OnboardingStep[][] = [];
  for (const step of required) {
    if (step.oneOf) {
      if (seen.has(step.oneOf)) continue;
      seen.add(step.oneOf);
      slots.push(required.filter((item) => item.oneOf === step.oneOf));
    } else {
      slots.push([step]);
    }
  }
  return slots;
}

export function slotDone(slot: OnboardingStep[], done: Record<string, boolean>): boolean {
  return slot.some((step) => done[step.id]);
}

export function progressCounts(steps: OnboardingStep[], done: Record<string, boolean>) {
  const slots = requiredSlots(steps);
  const complete = slots.filter((slot) => slotDone(slot, done)).length;
  return { complete, total: slots.length };
}

export function activeStep(pathname: string, steps: OnboardingStep[], done: Record<string, boolean>, dismissed: Record<string, boolean>): OnboardingStep | null {
  const matches = steps.filter((step) => longestMatchLength(pathname, step) > 0);
  matches.sort((a, b) => {
    const byLength = longestMatchLength(pathname, b) - longestMatchLength(pathname, a);
    if (byLength !== 0) return byLength;
    if (!!a.optional !== !!b.optional) return a.optional ? 1 : -1;
    if (!!a.later !== !!b.later) return a.later ? 1 : -1;
    return 0;
  });
  return matches.find((step) => !done[step.id] && !dismissed[step.id]) ?? null;
}

function storageKey(userKey: string) {
  return `realesy.brokerageOnboarding.v1.${userKey}`;
}

export function loadProgress(userKey: string): OnboardingProgress {
  try {
    const raw = localStorage.getItem(storageKey(userKey));
    if (!raw) return { ...EMPTY, done: {}, dismissedHints: {} };
    const parsed = JSON.parse(raw) as Partial<OnboardingProgress>;
    return {
      done: parsed.done && typeof parsed.done === "object" ? parsed.done : {},
      dismissedHints: parsed.dismissedHints && typeof parsed.dismissedHints === "object" ? parsed.dismissedHints : {},
      collapsed: Boolean(parsed.collapsed),
      hidden: Boolean(parsed.hidden),
    };
  } catch {
    return { ...EMPTY, done: {}, dismissedHints: {} };
  }
}

export function saveProgress(userKey: string, progress: OnboardingProgress) {
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(progress));
  } catch {
    /* private mode or a full disk should not block the page */
  }
}
